import type { SetlistContext } from './client';
import { STOCK_VIDEO_CATEGORIES } from './collections';
import type { VideoCategory } from './stock-video-types';
import { getCategoryStatusFilter } from './stock-video-types';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

export interface RankedVideoCategory {
  category: VideoCategory;
  vectorDistance: number;
}

/**
 * Fetches video categories within a group using embedding-based vector search.
 * Uses multi-search endpoint (POST) to avoid URL length limits with large embeddings.
 */
export const getVideoCategoriesByGroup = async (
  context: SetlistContext,
  groupEmbedding: number[],
  excludeName?: string,
  distanceThreshold = VECTOR_SEARCH_DEFAULTS.CATEGORY.DISTANCE_THRESHOLD,
  k = VECTOR_SEARCH_DEFAULTS.CATEGORY.K,
  isAdmin = false
): Promise<RankedVideoCategory[]> => {
  if (!groupEmbedding || groupEmbedding.length === 0) {
    return [];
  }

  // Use multi-search endpoint (POST) to handle large embedding vectors
  // When distance_threshold < 1.0, omit k parameter to let threshold do the filtering
  // When distance_threshold >= 1.0 (no filtering), use k to limit results
  const vectorParams =
    distanceThreshold < 1.0
      ? `distance_threshold:${distanceThreshold}`
      : `k:${k}`;

  const statusFilter = getCategoryStatusFilter(isAdmin);
  const filters = [statusFilter];
  if (excludeName) {
    filters.push(`name:!=[${excludeName}]`);
  }

  const multiSearchResponse = await context.client.multiSearch.perform(
    {
      searches: [
        {
          collection: STOCK_VIDEO_CATEGORIES,
          q: '*',
          vector_query: `embedding:([${groupEmbedding.join(',')}], ${vectorParams})`,
          // Fetch double the display limit so deduplication (handled in the
          // component) has a buffer of candidates to draw from after removing
          // categories that scored better in another group.
          per_page: 24,
          prefix: false,
          sort_by: '_vector_distance:asc',
          filter_by: filters.join(' && '),
        },
      ],
    },
    {},
    { cacheSearchResultsForSeconds: 60 }
  );

  const result = multiSearchResponse.results[0] as {
    hits?: Array<{
      document: VideoCategory;
      vector_distance?: number;
    }>;
  };
  return (
    result.hits?.map((hit) => ({
      category: hit.document,
      vectorDistance: hit.vector_distance ?? 0,
    })) ?? []
  );
};
